import { useState } from "react";
import type { ReviewTileItem, SearchResult } from "@/types/tile.types";
import { useFuzzySearch } from "./useFuzzySearch";
import { useTileTextExtraction } from "./useTileTextExtraction";
import {
  UseTileSearchOptions,
  normalizeTextWith,
  buildFindMatchesFn,
  executeSearchInDocument,
  executeClearHighlights,
  executeSearchInDocumentWithTile,
  wrapWithSearchingState,
  createSearchTilesCallback,
  createSearchByFieldCallback,
  createFuzzySearchCallback,
} from "./tile-search.utils";

export { normalizeTextWith };

export const useTileSearch = (options?: UseTileSearchOptions) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const { fuzzySearchInDocument } = useFuzzySearch();
  const { getSearchableText, normalizeSearchText } = useTileTextExtraction();

  const caseSensitive = options?.caseSensitive ?? false;
  const wholeWords = options?.wholeWords ?? false;
  const normalizeText = (text: string): string => normalizeTextWith(text, caseSensitive);
  const findMatches = buildFindMatchesFn(caseSensitive, wholeWords);
  const searchInDocument = (term: string) =>
    executeSearchInDocument(term, caseSensitive, wholeWords, options);
  const searchTilesCallback = createSearchTilesCallback(
    findMatches,
    normalizeText,
    setSearchResults,
    setCurrentSearchTerm,
    setSearchHistory,
    options
  );
  const searchTiles = wrapWithSearchingState(setIsSearching, searchTilesCallback);
  return {
    isSearching,
    searchResults,
    currentSearchTerm,
    searchHistory,
    searchTiles,
    searchInDocument,
    searchInDocumentWithTile: (item: ReviewTileItem) =>
      executeSearchInDocumentWithTile(
        item,
        fuzzySearchInDocument,
        getSearchableText,
        normalizeSearchText,
        options
      ),
    clearHighlights: () => executeClearHighlights(options),
    searchByField: wrapWithSearchingState(
      setIsSearching,
      createSearchByFieldCallback(findMatches, setSearchResults, options)
    ),
    fuzzySearch: wrapWithSearchingState(
      setIsSearching,
      createFuzzySearchCallback(normalizeText, setSearchResults, options)
    ),
    clearSearchResults: (): void => {
      setSearchResults([]);
      setCurrentSearchTerm("");
    },
    getSearchHistory: (): string[] => searchHistory,
    clearSearchHistory: (): void => {
      setSearchHistory([]);
    },
  };
};
